import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { Bar, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CardShell } from '../../atlas/cards/CardShell';
import { MetricRow } from '../../atlas/cards/MetricRow';
import { countYAxisReferenceGridLines } from '../../countYAxisReferenceGridLines';
import { renderChartGoalLines } from '../../ChartGoalLines';
import {
  aggregateQuarterlyChartToAnnual,
  attachMappedGoalTargets,
  buildCountYAxisScaleForChart,
  calcYtdYoYChange,
  collectGoalValues,
  formatYoYSublabel,
  latest,
  parseQuarter,
} from '../componentProjectMetricUtils';
import type { AswpInsightsData } from './mapAswpComponentProjectData';

type AswpInsightsCardProps = {
  data: AswpInsightsData;
};

export function AswpInsightsCard({ data }: AswpInsightsCardProps) {
  const theme = useMantineTheme();
  const series = data.series;
  const cur = latest(series);
  const subtitle = cur?.quarter ?? 'No data';

  const currentMeta = parseQuarter(cur?.quarter ?? '');
  const ytd = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + point.insightsGenerated, 0)
    : 0;

  const insightsAnnualGoal = cur?.goalTarget;
  const insightsYoY = cur
    ? calcYtdYoYChange(series, cur.quarter, (point) => point.quarter, (point) => point.insightsGenerated)
    : null;

  const quarterlyChartData = attachMappedGoalTargets(
    series.map((point) => ({
      quarter: point.quarter,
      insightsGenerated: point.insightsGenerated,
    })),
    series.map((point) => ({
      goalTargets: {
        insightsGenerated: point.goalTarget,
      },
    })),
    [{ dataKey: 'insightsGenerated', goalField: 'insightsGenerated' }]
  );

  const chartData = aggregateQuarterlyChartToAnnual(quarterlyChartData, ['insightsGenerated']);

  const goalValues = collectGoalValues(chartData, ['insightsGenerated']);
  const { yAxisMax, yAxisTicks } = buildCountYAxisScaleForChart(
    chartData.map((point) => Number(point.insightsGenerated)),
    goalValues
  );
  const gridLineStroke =
    theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.12)';

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 280,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const hasChartPoints = series.length > 0;

  return (
    <CardShell title="Insights Generated" subtitle={subtitle}>
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'Insights (YTD)',
              labelTooltip:
                'Calendar year to date insights generated across ASWP (sum of quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {ytd}
                </Text>
              ),
              sublabel:
                insightsAnnualGoal !== undefined ? (
                  <>
                    {formatYoYSublabel(insightsYoY)}
                    <br />
                    {`Goal ${insightsAnnualGoal}/year`}
                  </>
                ) : (
                  formatYoYSublabel(insightsYoY)
                ),
              reserveGoalSublabel: false,
            },
            {
              label: 'Insights (All-Time)',
              labelTooltip: 'Total insights generated across all reported quarters.',
              value: (
                <Text size="lg" weight={800}>
                  {data.allTime}
                </Text>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Annual Insights
        </Text>

        <Box sx={{ height: 170 }}>
          {hasChartPoints ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.12} vertical={false} />
                <XAxis
                  dataKey="yearLabel"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={12}
                />
                <YAxis
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  width={44}
                  tickMargin={8}
                  domain={[0, yAxisMax]}
                  ticks={yAxisTicks}
                />
                {countYAxisReferenceGridLines(yAxisTicks, gridLineStroke, goalValues)}
                {renderChartGoalLines(chartData, ['insightsGenerated'])}
                <Tooltip
                  cursor={false}
                  content={({ active, payload, label }) => {
                    if (!active || !payload || payload.length === 0) return null;
                    const value = Number(payload[0]?.value ?? 0);
                    const unit = value === 1 ? 'Insight' : 'Insights';
                    return (
                      <div style={tooltipShellStyle}>
                        <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>
                          {label}
                        </div>
                        <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2 }}>
                          {payload[0]?.value ?? '—'} {unit}
                        </div>
                      </div>
                    );
                  }}
                />
                <Bar
                  dataKey="insightsGenerated"
                  fill="#FAB005"
                  barSize={18}
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={false}
                  activeBar={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <Text
              size="sm"
              color="dimmed"
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
            >
              No annual insights data available.
            </Text>
          )}
        </Box>
      </Stack>
    </CardShell>
  );
}
